/**
 * Step-up password dialog (§7.1).
 *
 * Any destructive action (delete agent, rotate OAuth, restore backup,
 * cancel deploy) requires the user to re-enter their password here.
 * The backend tracks elevation on the session — there is no token to
 * pass explicitly; the caller just needs to know elevation succeeded
 * and can then issue the protected request.
 *
 * The `onSuccess` callback is invoked with a short marker string so
 * existing call sites can keep their "pass token through" shape
 * without actually relying on the value.
 */
import { useEffect, useState } from 'react';
import { KeyRound } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { authApi } from '@/api/auth';
import { ApiError } from '@/api/client';

export interface StepUpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired after the session has been elevated. */
  onSuccess: (marker: string) => void | Promise<void>;
  title?: string;
  description?: string;
}

export function StepUpDialog({
  open,
  onOpenChange,
  onSuccess,
  title = 'Confirm your password',
  description = 'This action requires you to re-authenticate. Your elevation is valid for a few minutes.',
}: StepUpDialogProps) {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setPassword('');
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await authApi.stepup({ password });
      await onSuccess(res.expires_at);
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.detail ?? err.title ?? 'Invalid password.');
      } else {
        setError('Unexpected error. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              {title}
            </DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Input
              autoFocus
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              aria-label="Password"
            />
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!password || submitting}>
              {submitting ? 'Verifying…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
